// Top-level layout: header (with tab bar) + main content area.
// Holds the global apiKey and active tab; routes content per tab.

import { useEffect, useState, type ReactNode } from "react";
import { Header } from "./Header";
import { ServicesDownBanner } from "./ServicesDownBanner";
import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { useServiceHealth } from "../../hooks/useServiceHealth";
import { getStoredApiKey, setStoredApiKey } from "../../api/client";
import { NavigationProvider, type TabId } from "../../state/NavigationContext";

export type { TabId };

interface Props {
  pages: Record<TabId, ReactNode>;
  initialTab?: TabId;
}

export function AppShell({ pages, initialTab = "builder" }: Props) {
  const [apiKey, setApiKey] = useState<string>(
    () => getStoredApiKey() || "dev-public-key-do-not-use-in-prod",
  );
  const [activeTab, setActiveTab] = useState<TabId>(initialTab);
  const health = useServiceHealth();

  // Persist API key on change so reloads keep the same credential without re-typing.
  useEffect(() => {
    setStoredApiKey(apiKey);
  }, [apiKey]);

  return (
    <TooltipProvider delayDuration={120}>
      <div className="flex flex-col h-screen p-4 gap-3 bg-background text-foreground">
        <Header
          apiKey={apiKey}
          onApiKeyChange={setApiKey}
          health={health}
          activeTab={activeTab}
          onTabChange={setActiveTab}
        />
        <ServicesDownBanner state={health} />
        <NavigationProvider navigateTo={setActiveTab}>
          <main className="flex-1 min-h-0 overflow-auto">{pages[activeTab]}</main>
        </NavigationProvider>
        <Toaster />
      </div>
    </TooltipProvider>
  );
}
